import type { CurrentUser } from "@/features/auth/types"
import { canVoid } from "@/features/auth/role"
import type { InvoiceStatus } from "@/components/status-badge"

import type { BackendStatus } from "./types"

/**
 * The full union of status values rows may carry on the
 * ``/invoices`` table. ``BackendStatus`` covers everything the
 * mutating endpoints accept; ``StatusBadge`` additionally renders
 * ``partially_paid`` / ``refunded`` / ``partially_refunded`` /
 * ``disputed``. We allow
 * both here so the bulk-selection helper can be called with raw
 * row data without a cast at every call site, while still catching
 * unknown literals at compile time (typos do not silently widen
 * to ``string``).
 */
export type SelectionStatus = BackendStatus | InvoiceStatus

export type InvoiceAction =
  | "view"
  | "void"
  | "resend"
  | "amend"
  | "reissue"
  | "copyLink"
  // Copies the customer self-service portal link (docs/design/customer-portal.md).
  // Gated alongside ``copyLink`` (the payable/actionable window); customers can
  // also self-serve at /portal by entering their email, so this is a Manager
  // convenience rather than the only path in.
  | "copyPortalLink"
  | "downloadPdf"
  | "delete"
  // Offered only on ``created`` (issued without email): delivers the
  // customer email and moves the invoice to ``sent``. Routes to the same
  // /send/ endpoint as ``resend`` but is labelled "Send email" and gated
  // on the never-emailed state (create-invoice-without-sending-email).
  | "sendEmail"

const ACTIONABLE_STATUSES: ReadonlySet<BackendStatus> = new Set([
  "sent",
  // ``created`` (issued without emailing the customer) is Sent-shaped for
  // every rep action — voidable, amendable, copy-link / download-PDF — so
  // it joins the actionable set. Its distinct "Send email" action is added
  // separately in ``actionsForStatus``.
  "created",
  "viewed",
  "overdue",
  "payment_failed",
])

// Statuses for which the detail-page Send button is offered. ``email_failed``
// joins ``draft`` because the customer never received an email — clicking
// Send hits the same backend endpoint and either delivers or stays in
// EMAIL_FAILED. See design §5.2 / §5.5.
export const SENDABLE_STATUSES: ReadonlySet<BackendStatus> = new Set([
  "draft",
  "email_failed",
])

// The voidable, still-active statuses — exported so the deal-resolution
// classifier (``useOpenInvoicesForDeal``) and the duplicate-invoice
// prompts read the one canonical partition instead of re-listing
// statuses. Aliased to ``ACTIONABLE_STATUSES`` (void is offered exactly
// where the row is actionable).
export const VOIDABLE_STATUSES: ReadonlySet<BackendStatus> = ACTIONABLE_STATUSES
// Amend (edit discounts) is offered on exactly the sent-but-unpaid set —
// the same partition as void (design §1). The no-installment-plan gate is
// applied at the call site (the action menu / detail buttons), which has
// the plan info the list-row status alone doesn't carry.
export const AMENDABLE_STATUSES: ReadonlySet<BackendStatus> =
  ACTIONABLE_STATUSES
// Resend is broader than the rest of ACTIONABLE_STATUSES because
// ``email_failed`` is also resendable from the list — the customer
// never received an email, so the FE routes the call to /send/ (the
// backend endpoint that accepts EMAIL_FAILED) while the UI label
// remains "Resend email" for operator consistency. void / copyLink /
// downloadPdf still require a delivered email, so they continue to
// gate on ACTIONABLE_STATUSES only.
const RESENDABLE_STATUSES: ReadonlySet<BackendStatus> = new Set([
  "sent",
  "viewed",
  "overdue",
  "payment_failed",
  "email_failed",
])
const COPY_PDF_STATUSES: ReadonlySet<BackendStatus> = ACTIONABLE_STATUSES

const SINGLE_ONLY_ACTIONS: ReadonlySet<InvoiceAction> = new Set([
  "reissue",
  "copyLink",
  "copyPortalLink",
  "downloadPdf",
  "view",
  // "Send email" is a per-invoice action offered on the row menu / detail
  // page only — no bulk-send strip in v1.
  "sendEmail",
  // Amend opens a per-invoice modal; it has no multi-row semantic, so it
  // is stripped from the bulk-selection strip.
  "amend",
])
const MUTATING_ACTIONS: ReadonlySet<InvoiceAction> = new Set([
  "void",
  "resend",
  // Send email transitions the invoice (created → sent), so it's
  // ownership-gated like the other mutators. Single-only, so already
  // stripped from multi-row selections.
  "sendEmail",
  // Amend mutates the invoice, so it's subject to the same ownership gate
  // as void/resend (a non-owner sales-rep must not see it). It's also
  // single-only, so it's already stripped from multi-row selections.
  "amend",
  // Delete removes the draft, so it's ownership-gated like the others (a
  // non-owner sales-rep must not see the bulk button). It survives
  // multi-row selections — bulk-delete of DRAFT rows is offered on the
  // selection strip — but the intersection naturally gates it to
  // all-draft selections (only ``draft`` yields ``delete`` above).
  "delete",
])

export function actionsForStatus(
  status: BackendStatus,
): Set<InvoiceAction> {
  const actions = new Set<InvoiceAction>(["view"])
  // A draft is the only status the self-service DELETE endpoint accepts
  // (design §3.1 / OQ-1). The ownership gate (SR own / Manager any) is
  // applied at the call site via ``canDelete``.
  if (status === "draft") actions.add("delete")
  if (VOIDABLE_STATUSES.has(status)) actions.add("void")
  if (AMENDABLE_STATUSES.has(status)) actions.add("amend")
  if (RESENDABLE_STATUSES.has(status)) actions.add("resend")
  // A ``created`` invoice was never emailed — offer "Send email" (routes
  // to /send/) instead of the "Resend email" the delivered statuses get.
  if (status === "created") actions.add("sendEmail")
  if (status === "voided") actions.add("reissue")
  if (COPY_PDF_STATUSES.has(status)) {
    actions.add("copyLink")
    // The customer-portal link sits beside the payment link (design file
    // plan: "gate like copyLink"). Customers can also self-serve at /portal
    // by entering their email, so this is a convenience, not the only path.
    actions.add("copyPortalLink")
    actions.add("downloadPdf")
  }
  return actions
}

// ---------------------------------------------------------------------------
// Deposit per-leg actions
// ---------------------------------------------------------------------------

/**
 * The actions a single deposit leg (deposit or balance) exposes in the
 * detail panel. ``copyLink`` copies that leg's pay URL, ``resend``
 * re-sends that leg's payment-link email, ``downloadReceipt`` links to
 * the per-leg receipt PDF.
 */
export type DepositLegAction = "copyLink" | "resend" | "downloadReceipt"

/** Minimal leg shape the gating helper reads. */
export interface DepositLegState {
  stage: "deposit" | "balance"
  status: string
  // When set, this leg's payment-link email has been issued at least
  // once (gates balance resend; deposit-payments spec §9).
  emailSentAt: Date | null
}

/**
 * Compute the enabled per-leg actions for a deposit leg, given the
 * invoice status. Mirrors the backend gating (deposit-payments spec §9
 * resend + §5.3 balance payability + §6.7 receipt gate):
 *
 *   - **copyLink** — always available (the rep can hand over either
 *     link), EXCEPT the balance link is suppressed until the deposit is
 *     paid (the balance leg's email_sent_at is set), per §10.3.
 *   - **resend** — deposit: while unpaid and the invoice is in a
 *     resendable status (sent/overdue/payment_failed/partially_paid).
 *     balance: only once the balance email has been issued
 *     (email_sent_at set) and the leg is unpaid.
 *   - **downloadReceipt** — once that leg's status === "paid".
 */
const LEG_RESENDABLE_INVOICE_STATUSES: ReadonlySet<BackendStatus> = new Set([
  "sent",
  "viewed",
  "overdue",
  "payment_failed",
  "partially_paid",
])

export function depositLegActions(
  leg: DepositLegState,
  invoiceStatus: BackendStatus,
): Set<DepositLegAction> {
  const actions = new Set<DepositLegAction>()
  const isPaid = leg.status === "paid"
  const emailIssued = leg.emailSentAt !== null

  // Copy link: the balance link is gated until the deposit is paid
  // (which is exactly when the balance email is issued). The deposit
  // link is always copyable.
  if (leg.stage === "deposit" || emailIssued) {
    actions.add("copyLink")
  }

  // Resend: never for a settled leg.
  if (!isPaid) {
    if (leg.stage === "deposit") {
      if (LEG_RESENDABLE_INVOICE_STATUSES.has(invoiceStatus)) {
        actions.add("resend")
      }
    } else if (emailIssued) {
      // Balance resend only once the balance email has been issued.
      actions.add("resend")
    }
  }

  // Receipt: once the leg has settled.
  if (isPaid) actions.add("downloadReceipt")

  return actions
}

/**
 * Map an invoice status to the duplicate-invoice resolution it implies
 * for a deal that already carries it:
 *
 *   - ``"void"`` — a voidable, still-active invoice
 *     (``sent``/``viewed``/``overdue``/``payment_failed``). The rep must
 *     void it before issuing a new one.
 *   - ``"open"`` — a draft/email_failed invoice still being worked. The
 *     rep should continue that row rather than start a second.
 *   - ``null`` — terminal/settled (``paid``/``expired``/``voided`` …). A
 *     fresh invoice is a legitimate reissue.
 *
 * ``void`` takes priority over ``open`` at the call site (design OQ-1):
 * when a deal has both, voiding the active invoice first is the
 * billing-safe path.
 */
export function classifyDealInvoiceStatus(
  status: BackendStatus,
): "void" | "open" | null {
  if (VOIDABLE_STATUSES.has(status)) return "void"
  if (SENDABLE_STATUSES.has(status)) return "open"
  return null
}

/**
 * Minimum row shape required by ``bulkActionsForSelection``. Matches
 * the ``Invoice`` row produced by the list mapper without binding to
 * the full type so the helper stays unit-testable.
 *
 * ``status`` is the union of every status the table may render
 * (``BackendStatus`` plus the read-only states surfaced by
 * ``StatusBadge``). Statuses outside ``BackendStatus``
 * (``refunded`` / ``partially_refunded`` / ``disputed``) fall
 * through ``actionsForStatus`` to a view-only set, which the
 * multi-row strip then collapses to empty — i.e. a disputed row in
 * the selection disables every bulk button, which is the right
 * defensive default.
 */
export interface BulkSelectionEntry {
  status: SelectionStatus
  createdById?: number
}

/**
 * Compute the set of actions enabled for the toolbar above the
 * invoices table, given the currently-selected rows and the signed-in
 * user.
 *
 * Rules:
 *   - Empty selection → empty set (every toolbar button is disabled).
 *   - Per-row enabled actions are intersected: a button is enabled
 *     only when every selected row supports it.
 *   - When ``invoices.length > 1``, the single-only actions
 *     (``reissue``, ``copyLink``, ``downloadPdf``, ``view``) are
 *     stripped — they have no natural multi-row semantic per the
 *     approved design.
 *   - The mutating actions (``void``, ``resend``, ``delete``) are
 *     stripped if any selected row fails ``canVoid(user, row)`` — Sales
 *     Reps may not bulk-act on invoices owned by a different rep, and
 *     Finance may not bulk-mutate at all. ``delete`` additionally
 *     survives only an all-``draft`` selection, since ``draft`` is the
 *     one status ``actionsForStatus`` grants it.
 */
export function bulkActionsForSelection(
  invoices: readonly BulkSelectionEntry[],
  user: CurrentUser | null | undefined,
): Set<InvoiceAction> {
  if (invoices.length === 0) return new Set<InvoiceAction>()

  let intersection: Set<InvoiceAction> | null = null
  for (const row of invoices) {
    const perRow = actionsForStatus(row.status as BackendStatus)
    if (intersection === null) {
      // Defensive copy: the helper mutates ``intersection`` below;
      // ``actionsForStatus`` always allocates a fresh Set today, but
      // copying here keeps the contract intact even if a future
      // memoization layer lands on top of ``actionsForStatus``.
      intersection = new Set(perRow)
    } else {
      for (const action of intersection) {
        if (!perRow.has(action)) intersection.delete(action)
      }
    }
  }
  const result = intersection ?? new Set<InvoiceAction>()

  if (invoices.length > 1) {
    for (const action of SINGLE_ONLY_ACTIONS) result.delete(action)
  }

  const ownershipOk = invoices.every((row) =>
    canVoid(
      user,
      row.createdById !== undefined
        ? { created_by: { id: row.createdById } }
        : null,
    ),
  )
  if (!ownershipOk) {
    for (const action of MUTATING_ACTIONS) result.delete(action)
  }

  return result
}
